import type { CallSheetMembershipDto, ProspectBookRowDto } from '@signalvote/shared';

/** Who can go on a call book: a number to ring, and not asked to be removed. */
export const canGoOnASheet = (row: ProspectBookRowDto) => !!row.prospect.phone?.trim() && !row.doNotCall;

/**
 * Each person's books, in the order the books come — built once, so "which books is this person on" is a
 * lookup rather than a scan of every book's members per person. Someone on no book isn't in it.
 */
export function booksByPerson(books: CallSheetMembershipDto[]): Map<string, CallSheetMembershipDto[]> {
  const map = new Map<string, CallSheetMembershipDto[]>();
  for (const book of books) {
    for (const id of new Set(book.prospectIds)) {
      const on = map.get(id);
      if (on) on.push(book);
      else map.set(id, [book]);
    }
  }
  return map;
}

/** Why someone won't be added to any of the ticked books. The server's own order: Do Not Call, already on it, no phone. */
export type SkipReason = 'doNotCall' | 'alreadyOn' | 'noPhone';

export interface CallSheetAddPlan {
  /** Per ticked book, who goes on it. A brand-new book is keyed `null`. */
  adds: { book: CallSheetMembershipDto | null; prospectIds: string[] }[];
  /** Everyone added to at least one book. */
  addedPeople: ProspectBookRowDto[];
  /** How many books get someone. */
  bookCount: number;
  /** People added to no book, by reason. */
  skipped: Record<SkipReason, ProspectBookRowDto[]>;
  /** People being added who are also on a book that isn't ticked — allowed, and said so. */
  alsoOn: { person: ProspectBookRowDto; books: string[] }[];
}

/**
 * What "Add to call books" will do, worked out from the book read before anyone confirms: who goes on each
 * ticked book (and a new one, when `creating`), who is skipped and why, and who is also on another book. The server
 * makes the same checks again when the add lands, so a stale read can never put someone on a book they can't be on.
 *
 * Each person is looked up in `onBooks` — everyone's books, `booksByPerson(books)` — so the work grows with the
 * selection and the few books each person is on, never selection × every book's people. A caller working it out
 * again as ticks change passes the same `onBooks` it built once for the books.
 */
export function planCallSheetAdd(
  people: ProspectBookRowDto[],
  books: CallSheetMembershipDto[],
  tickedIds: ReadonlySet<string>,
  creating: boolean,
  onBooks: Map<string, CallSheetMembershipDto[]> = booksByPerson(books),
): CallSheetAddPlan {
  const ticked = books.filter((book) => tickedIds.has(book.id));
  const skipped: CallSheetAddPlan['skipped'] = { doNotCall: [], alreadyOn: [], noPhone: [] };
  const addedPeople: ProspectBookRowDto[] = [];
  const alsoOn: CallSheetAddPlan['alsoOn'] = [];
  const perBook = new Map<string, string[]>(ticked.map((book) => [book.id, []]));

  for (const person of people) {
    const id = person.prospect.id;
    if (person.doNotCall) {
      skipped.doNotCall.push(person);
      continue;
    }
    const theirs = onBooks.get(id) ?? [];
    const alreadyOn = new Set(theirs.filter((book) => tickedIds.has(book.id)).map((book) => book.id));
    const goesOn = ticked.filter((book) => !alreadyOn.has(book.id));
    if (!creating && ticked.length > 0 && goesOn.length === 0) {
      skipped.alreadyOn.push(person);
      continue;
    }
    if (!canGoOnASheet(person)) {
      skipped.noPhone.push(person);
      continue;
    }
    if (!creating && ticked.length === 0) continue;
    addedPeople.push(person);
    for (const book of goesOn) perBook.get(book.id)!.push(id);
    const elsewhere = theirs.filter((book) => !tickedIds.has(book.id)).map((book) => book.name);
    if (elsewhere.length > 0) alsoOn.push({ person, books: elsewhere });
  }

  const adds: CallSheetAddPlan['adds'] = ticked
    .map((book) => ({ book, prospectIds: perBook.get(book.id)! }))
    .filter((add) => add.prospectIds.length > 0);
  if (creating && addedPeople.length > 0) adds.push({ book: null, prospectIds: addedPeople.map((p) => p.prospect.id) });

  return { adds, addedPeople, bookCount: adds.length, skipped, alsoOn };
}
